import { createAction, createReducer } from 'redux-act';
import moment from 'moment';
import Immutable from 'seamless-immutable';
import { comparisonOptionNames } from '../models';


const defaultComparisonDateRange = {
  comparisonId: 1,
  option: comparisonOptionNames.none,
  locationId: -1,
};

const computeComparisonOptions = (startDate, endDate) => [
  {
    name: comparisonOptionNames.none,
    pickerVisible: false,
    pickerDisabled: true,
  },
  {
    name: comparisonOptionNames.lastYear,
    pickerVisible: true,
    pickerDisabled: true,
    startDate: moment.unix(startDate).subtract(1, 'year').unix(),
    endDate: moment.unix(endDate).subtract(1, 'year').unix(),
  },
  {
    name: comparisonOptionNames.lastMonth,
    pickerVisible: true,
    pickerDisabled: true,
    startDate: moment.unix(startDate).subtract(1, 'month').unix(),
    endDate: moment.unix(endDate).subtract(1, 'month').unix(),
  },
  {
    name: comparisonOptionNames.lastWeek,
    pickerVisible: true,
    pickerDisabled: true,
    startDate: moment.unix(startDate).subtract(1, 'week').unix(),
    endDate: moment.unix(endDate).subtract(1, 'week').unix(),
  },
  {
    name: comparisonOptionNames.sameRange,
    pickerVisible: true,
    pickerDisabled: true,
    startDate: moment.unix(startDate).subtract(moment.duration(moment.unix(endDate).diff(moment.unix(startDate))))
      .unix(),
    endDate: moment.unix(startDate).unix(),
  },
  {
    name: comparisonOptionNames.identical,
    pickerVisible: true,
    pickerDisabled: true,
    startDate,
    endDate,
    skipSendInMessage: true,
  },
  {
    name: comparisonOptionNames.custom,
    pickerVisible: true,
    pickerDisabled: false,
    startDate: moment.unix(startDate).unix(),
    endDate: moment.unix(endDate).unix(),
    skipSendInMessage: true,
  },
];

const defaultStartDate = moment().set('hours', 5).set('minutes', 0).unix();
const defaultEndDate = moment().add(1, 'days').set('hours', 5).set('minutes', 0)
  .unix();

const INITIAL_STATE = Immutable({
  dateRange: {
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  },
  useDefaultDateRange: true,
  comparisonDateRanges: [defaultComparisonDateRange],
  useMultiLocationComparison: false,
  filterOptions: {}, // combines all further filters into one object that is converted to query-params
  areas: [],
  comparisonOptions: computeComparisonOptions(defaultStartDate, defaultEndDate),
  daytimeOptions: [
    { name: 'Morgens', startTime: '05:00', endTime: '11:00' },
    { name: 'Mittags', startTime: '11:00', endTime: '14:30' },
    { name: 'Nachmittag', startTime: '14:30', endTime: '18:00' },
    { name: 'Abends', startTime: '18:00', endTime: '05:00' },
  ],
});

export const setDateRange = createAction('SET_DATE_RANGE');
export const setUseDefaultDateRange = createAction('SET_USE_DEFAULT_DATE_RANGE');
export const setComparisonDateRange = createAction('SET_COMPARISON_DATE_RANGE');
export const removeComparisonDateRange = createAction('REMOVE_COMPARISON_DATE_RANGE');

export const loadAreas = createAction('LOAD_AREAS');
export const loadAreasSucceeded = createAction('LOAD_AREAS_SUCCEEDED');
export const loadAreasFailed = createAction('LOAD_AREAS_FAILED');

export const setFilterOption = createAction('SET_FILTER_OPTION');


const reducers = createReducer({
  [setDateRange]: (state, { startDate, endDate }) => {
    const comparisonOptions = computeComparisonOptions(startDate, endDate);

    return state.merge({
      dateRange: {
        startDate,
        endDate,
      },
      useDefaultDateRange: false,
      comparisonOptions,
    });
  },
  [setUseDefaultDateRange]: (state, useDefaultDateRange) => state.merge({ useDefaultDateRange }),
  [setComparisonDateRange]: (state, comparisonDateRange) => {
    if (!comparisonDateRange) {
      return state.merge({ comparisonDateRanges: [], useMultiLocationComparison: false });
    }

    const oldComparisonDateRanges = state.comparisonDateRanges.filter(d => d.comparisonId !== comparisonDateRange.comparisonId);

    const comparisonOption = state.comparisonOptions.find(co => co.name === comparisonDateRange.option);

    const { startDate, endDate } = comparisonOption && comparisonDateRange.option !== comparisonOptionNames.custom
      ? comparisonOption : comparisonDateRange;

    const newComparisonDateRanges = oldComparisonDateRanges.concat({
      comparisonId: comparisonDateRange.comparisonId,
      startDate,
      endDate,
      locationId: comparisonDateRange.locationId,
      option: comparisonDateRange.option,
    });

    const useMultiLocationComparison = newComparisonDateRanges
      .every(cdr => cdr.option === comparisonOptionNames.identical && cdr.locationId !== -1);

    return state.merge({
      comparisonDateRanges: newComparisonDateRanges,
      useMultiLocationComparison,
    });
  },
  [removeComparisonDateRange]: (state, comparisonId) => {
    const newComparisonDateRanges = state.comparisonDateRanges.filter(d => d.comparisonId !== comparisonId);

    if (newComparisonDateRanges.length === 0) {
      return state.merge({ comparisonDateRanges: [defaultComparisonDateRange], useMultiLocationComparison: false });
    }

    const useMultiLocationComparison = newComparisonDateRanges
      .every(cdr => cdr.option === comparisonOptionNames.identical && cdr.locationId !== -1);

    return state.merge({ comparisonDateRanges: newComparisonDateRanges, useMultiLocationComparison });
  },
  [loadAreasSucceeded]: (state, { areas }) => state.merge({ areas }),
  [setFilterOption]: (state, { key, value }) => {
    let filterOptions;
    if (value) {
      filterOptions = state.filterOptions.set(key, value);
    } else {
      filterOptions = state.filterOptions.without(key);
    }
    return state.merge({ filterOptions });
  },
}, INITIAL_STATE);

export default reducers;
